import React, { useEffect, useState } from "react";
import { Plus, Minus, TrendingDown, DollarSign } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CTABlock } from "@/components/common/CTABlock";
import { StarMotif } from "@/components/common/StarMotif";
import { Link } from "react-router-dom";

function currency(n) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

export default function CostExplorer() {
    useSEO({
        title: "Cost Explorer — Agile Team Estimator | RKS Systems",
        description: "Model an RKS Systems agile team pod and compare monthly cost vs US onshore. Choose roles, quantity, and hours/month.",
    });

    const [roles, setRoles] = useState([]);
    const [challenges, setChallenges] = useState([]);
    const [challenge, setChallenge] = useState("");
    const [selection, setSelection] = useState([{ role: "Full Stack Engineer", quantity: 2, hours_per_month: 160 }]);
    const [estimate, setEstimate] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/cost-explorer/roles").then(({ data }) => setRoles(data.roles || []));
        api.get("/cost-explorer/challenges").then(({ data }) => setChallenges(data.challenges || []));
    }, []);

    const recompute = async (rows) => {
        try {
            const { data } = await api.post("/cost-explorer/estimate", { roles: rows, challenge: challenge || null });
            setEstimate(data);
            setError("");
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    useEffect(() => { if (roles.length > 0) recompute(selection); /* eslint-disable-next-line */ }, [selection, roles.length]);

    const onChallengeChange = (value) => {
        setChallenge(value);
        const found = challenges.find((c) => c.name === value);
        if (found && (found.recommended_roles || []).length > 0) {
            const roleNames = new Set(roles.map((r) => r.name));
            const prefilled = found.recommended_roles
                .filter((rn) => roleNames.has(rn))
                .map((rn) => ({ role: rn, quantity: 1, hours_per_month: 160 }));
            if (prefilled.length > 0) setSelection(prefilled);
        }
    };

    const selectedChallenge = challenges.find((c) => c.name === challenge);

    const addRow = () => setSelection([...selection, { role: roles[0]?.name || "Full Stack Engineer", quantity: 1, hours_per_month: 160 }]);
    const updateRow = (idx, key, value) => {
        const next = [...selection];
        next[idx] = { ...next[idx], [key]: key === "role" ? value : Math.max(1, Number(value) || 1) };
        setSelection(next);
    };
    const removeRow = (idx) => setSelection(selection.filter((_, i) => i !== idx));

    return (
        <div data-testid="page-cost-explorer">
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-20 lg:py-28">
                    <div className="max-w-3xl">
                        <h1 className="mt-4 font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
                            Model your team.<br/><span className="text-primary">See the savings.</span>
                        </h1>
                        <p className="mt-6 text-lg text-white/70 leading-relaxed">
                            Pick the roles, quantity and hours per month. We&apos;ll show you a realistic RKS estimate against a US onshore benchmark — with no salespeople in the loop.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-[hsl(222_47%_9%)] text-white">
                <div className="container-app grid lg:grid-cols-[1.4fr,1fr] gap-8">
                    {/* Builder */}
                    <div className="rounded-2xl border border-white/10 bg-[hsl(222_47%_11%)] p-6 lg:p-8" data-testid="cost-builder">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-heading text-2xl font-bold">Build your team</h2>
                            <button
                                type="button"
                                onClick={addRow}
                                data-testid="cost-add-role"
                                className="inline-flex items-center gap-1.5 rounded-md bg-primary/90 hover:bg-primary px-3 py-2 text-xs font-semibold text-white btn-lift"
                            >
                                <Plus className="h-3.5 w-3.5" /> Add role
                            </button>
                        </div>
                        <div className="mb-6 rounded-lg bg-white/[0.03] border border-white/10 p-4">
                            <label className="text-xs font-semibold uppercase tracking-widest text-white/60">What engineering initiative are you looking to accelerate?</label>
                            <select
                                data-testid="cost-challenge-select"
                                value={challenge}
                                onChange={(e) => onChallengeChange(e.target.value)}
                                className="mt-1.5 w-full rounded-md bg-[hsl(222_47%_7%)] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                <option value="">Select an engineering challenge…</option>
                                {challenges.map((c) => (<option key={c.name} value={c.name}>{c.name}</option>))}
                            </select>
                            {selectedChallenge?.description && (
                                <p className="mt-2 text-xs text-white/50 leading-relaxed" data-testid="cost-challenge-description">{selectedChallenge.description}</p>
                            )}
                            {selectedChallenge && (
                                <p className="mt-1.5 text-[11px] text-primary/90">We&apos;ve suggested a starter team below — adjust roles, quantity and hours to fit your scope.</p>
                            )}
                        </div>
                        <div className="space-y-4">
                            {selection.map((row, idx) => (
                                <div key={idx} data-testid={`cost-row-${idx}`} className="grid grid-cols-12 gap-3 items-end p-4 rounded-lg bg-white/[0.03] border border-white/10">
                                    <div className="col-span-12 md:col-span-5">
                                        <label className="text-xs font-semibold uppercase tracking-widest text-white/60">Role</label>
                                        <select
                                            data-testid={`cost-role-select-${idx}`}
                                            value={row.role}
                                            onChange={(e) => updateRow(idx, "role", e.target.value)}
                                            className="mt-1.5 w-full rounded-md bg-[hsl(222_47%_7%)] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary"
                                        >
                                            {roles.map((r) => (<option key={r.name} value={r.name}>{r.name}</option>))}
                                        </select>
                                    </div>
                                    <div className="col-span-6 md:col-span-3">
                                        <label className="text-xs font-semibold uppercase tracking-widest text-white/60">Quantity</label>
                                        <input
                                            type="number"
                                            min="1"
                                            data-testid={`cost-quantity-${idx}`}
                                            value={row.quantity}
                                            onChange={(e) => updateRow(idx, "quantity", e.target.value)}
                                            className="mt-1.5 w-full rounded-md bg-[hsl(222_47%_7%)] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary"
                                        />
                                    </div>
                                    <div className="col-span-6 md:col-span-3">
                                        <label className="text-xs font-semibold uppercase tracking-widest text-white/60">Duration (Hours)</label>
                                        <input
                                            type="number"
                                            min="1"
                                            data-testid={`cost-duration-${idx}`}
                                            value={row.hours_per_month}
                                            onChange={(e) => updateRow(idx, "hours_per_month", e.target.value)}
                                            className="mt-1.5 w-full rounded-md bg-[hsl(222_47%_7%)] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary"
                                        />
                                    </div>
                                    <div className="col-span-12 md:col-span-1 flex md:justify-end">
                                        {selection.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeRow(idx)}
                                                data-testid={`cost-remove-${idx}`}
                                                className="mt-1.5 inline-flex h-10 w-10 items-center justify-center rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/40"
                                                aria-label="Remove role"
                                            >
                                                <Minus className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {error && <div className="mt-4 rounded-md border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>}
                    </div>

                    {/* Summary */}
                    <div className="lg:sticky lg:top-24 self-start rounded-2xl border border-primary/30 bg-gradient-to-b from-primary/10 to-transparent p-6 lg:p-8" data-testid="cost-summary">
                        <div className="flex items-center gap-2 text-primary">
                            <StarMotif className="h-5 w-5" />
                            <h2 className="font-heading text-2xl font-bold text-white">RKS Systems Agile Team Summary</h2>
                        </div>

                        {/* Table */}
                        <div className="mt-6 overflow-hidden rounded-xl border border-white/10 bg-[hsl(222_47%_11%)]" data-testid="cost-summary-table">
                            <table className="w-full text-sm">
                                <thead className="bg-white/[0.04]">
                                    <tr>
                                        <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-white/60">Role</th>
                                        <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-white/60">Qty</th>
                                        <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-white/60">Hours/Mo</th>
                                        <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-white/60">Rate (Hr)</th>
                                        <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-white/60">Monthly Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(estimate?.breakdown || []).map((row, i) => (
                                        <tr key={i} data-testid={`cost-summary-row-${i}`} className="border-t border-white/10">
                                            <td className="px-3 py-2.5 text-white/90">{row.role}</td>
                                            <td className="px-3 py-2.5 text-right text-white/85">{row.quantity}</td>
                                            <td className="px-3 py-2.5 text-right text-white/85">{row.hours_per_month}</td>
                                            <td className="px-3 py-2.5 text-right text-white/85">{currency(row.rks_hourly_rate)}</td>
                                            <td className="px-3 py-2.5 text-right font-semibold text-white">{currency(row.monthly_cost_rks)}</td>
                                        </tr>
                                    ))}
                                    <tr className="border-t-2 border-primary/40 bg-primary/10">
                                        <td colSpan="4" className="px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-primary">Total monthly (RKS)</td>
                                        <td className="px-3 py-2.5 text-right font-heading font-extrabold text-white" data-testid="cost-monthly-rks-total">{currency(estimate?.monthly_rks)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Tiles */}
                        <div className="mt-5 grid grid-cols-3 gap-3">
                            <div className="rounded-lg bg-white/[0.04] px-4 py-3" data-testid="cost-tile-us">
                                <div className="text-[10px] text-white/55 uppercase tracking-widest">US Onshore Cost</div>
                                <div className="mt-1.5 font-semibold text-white/85">{currency(estimate?.monthly_us)}</div>
                                <div className="text-[10px] text-white/40 mt-0.5">per month</div>
                            </div>
                            <div className="rounded-lg bg-white/[0.04] px-4 py-3" data-testid="cost-tile-rks">
                                <div className="text-[10px] text-white/55 uppercase tracking-widest">RKS Systems Cost</div>
                                <div className="mt-1.5 font-semibold text-white">{currency(estimate?.monthly_rks)}</div>
                                <div className="text-[10px] text-white/40 mt-0.5">per month</div>
                            </div>
                            <div className="rounded-lg bg-primary/15 border border-primary/40 px-4 py-3" data-testid="cost-tile-savings">
                                <div className="text-[10px] text-primary/90 uppercase tracking-widest flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Expected Savings</div>
                                <div className="mt-1.5 font-semibold text-white">{currency(estimate?.savings_usd)}</div>
                                <div className="text-[10px] text-primary/90 mt-0.5">{estimate?.savings_pct ?? 0}% saved</div>
                            </div>
                        </div>

                        <div className="mt-8 flex flex-col gap-2">
                            <Link to={`/request-quote${challenge ? `?challenge=${encodeURIComponent(challenge)}` : ""}`} data-testid="cost-request-quote-btn" className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-white btn-lift hover:bg-primary/90">
                                <DollarSign className="h-4 w-4" /> Request a Quote with this Agile Team Pod
                            </Link>
                            <Link to="/schedule-meeting" data-testid="cost-schedule-meeting-btn" className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 bg-white/[0.04] px-5 py-3 font-semibold text-white hover:bg-white/[0.08]">
                                Talk to a delivery lead
                            </Link>
                        </div>
                        <p className="mt-6 text-xs text-white/50 leading-relaxed">
                            Estimates are indicative. Final pricing depends on scope, seniority mix, and engagement model. US benchmark rates are illustrative averages for equivalent roles.
                        </p>
                    </div>
                </div>
            </section>
            <CTABlock />
        </div>
    );
}
